import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  CTASection,
} from "../../components/marketing-components.js";

export function ManualReviewPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Professional manual review services"
        subtitle="When automated testing isn't enough, get expert accessibility review"
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>Automated testing + manual expertise</h2>

          <p>
            Our platform finds many issues automatically. But accessibility is
            about real users. For high-confidence, high-stakes sites, manual
            review by experts is essential.
          </p>

          <p style={{ marginTop: "16px" }}>
            We partner with experienced accessibility consultants to provide:
          </p>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>When you should consider manual review</h2>

          <div className="consideration-list">
            <div className="consideration">
              <h4>Legal or healthcare sites</h4>
              <p>
                High risk of liability. Automated testing alone isn't sufficient
                due diligence.
              </p>
            </div>

            <div className="consideration">
              <h4>E-commerce with high transaction value</h4>
              <p>
                Accessibility barriers = lost sales. Manual testing of checkout
                flow is critical.
              </p>
            </div>

            <div className="consideration">
              <h4>Complex multi-step processes</h4>
              <p>
                Loan applications, enrollment, configuration. Edge cases and
                workflows need human testing.
              </p>
            </div>

            <div className="consideration">
              <h4>Government or public sector</h4>
              <p>
                Often legally required. Manual testing + documentation
                strengthen your position.
              </p>
            </div>

            <div className="consideration">
              <h4>After major design changes</h4>
              <p>
                New components, new interactions. Before launch, have a real
                user test it.
              </p>
            </div>

            <div className="consideration">
              <h4>To improve confidence</h4>
              <p>
                You want to know aspects that automation can't measure: screen
                reader experience, mobile usability, cognitive clarity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>What manual review includes</h2>

          <div className="service-list">
            <div className="service">
              <h4>Screen reader testing</h4>
              <p>
                Real testing with NVDA, JAWS, and VoiceOver on actual pages. No
                guessing about user experience.
              </p>
            </div>

            <div className="service">
              <h4>Keyboard navigation audit</h4>
              <p>
                Tab order, focus management, hidden content. Step through
                critical workflows keyboard-only.
              </p>
            </div>

            <div className="service">
              <h4>Mobile accessibility</h4>
              <p>
                Touch targets, mobile screen readers, zoom behavior. Desktop
                testing misses mobile complexity.
              </p>
            </div>

            <div className="service">
              <h4>Content and UX review</h4>
              <p>
                Is alt text good? Are error messages clear? Is the design
                understandable? These need human judgment.
              </p>
            </div>

            <div className="service">
              <h4>Remediation guidance</h4>
              <p>
                Not just "you have a problem," but "here's how to fix it" with
                code examples and priorities.
              </p>
            </div>

            <div className="service">
              <h4>Legal/compliance documentation</h4>
              <p>
                Clear report for audits, insurance, or legal defense. Signed by
                certified professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Continuous partnership</h2>

          <p>
            Manual review isn't one-time. As you remediate automated findings,
            manual review validates fixes and tests new functionality.
          </p>

          <ul className="partnership-list">
            <li>Initial comprehensive review (2-4 weeks)</li>
            <li>Remediation guidance and prioritization</li>
            <li>Re-test specific components after fixes</li>
            <li>Quarterly or annual full re-tests</li>
            <li>Training for your team on best practices</li>
          </ul>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>How it integrates with AccessiMonitor</h2>

          <p>
            Your automated scans run monthly. Manual review happens
            periodically, then you get back more specific findings to prioritize
            in your next cycles.
          </p>

          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <strong>Workflow:</strong>
            <ol style={{ marginTop: "12px" }}>
              <li>
                Month 1: Automated baseline scan + manual code/content review
              </li>
              <li>Month 2: Automated scan, you start fixes</li>
              <li>Month 3: Automated scan, manual re-test of specific fixes</li>
              <li>Month 4: Automated scan, continue fixes</li>
              <li>Month 6: Full manual re-test to validate progress</li>
            </ol>
          </div>
        </div>
      </section>

      <CTASection
        title="Schedule a manual review"
        description="Talk to our experts about a review that fits your site's needs and risk profile."
        primaryCTA={{ label: "Contact us", href: "/contact" }}
      />
    </PublicLayout>
  );
}
